<template>
    <div class="account-page-padding">
        <h1>Password and Security</h1>
        <h3>Change Password</h3>
        <form @submit.prevent="login" class="authorization-form password-change">
            <PasswordInput 
                v-model="form.new_password" 
                :errors="form.errors.new_password" 
                id="new_password" 
                placeholder="New Password" 
            />
            <PasswordInput 
                v-model="form.confirm_new_password" 
                :errors="form.errors.confirm_new_password" 
                id="confirm_new_password" 
                placeholder="Confirm New Password" 
            />
            <RothcoCta id="submit" type="button" @click="submitForm()" class="primary" aria-label="Submit Password Change" :disabled="form.processing">
                Submit
            </RothcoCta>
            <div v-if="show_success" class="field-state">
                <div class="notification notification-success">
                    <p class="paragraph-details"><strong>Password Changed!</strong></p>
                </div>
            </div>
        </form>
        <h3>Two-Factor Authentication</h3>
        <div class="flex-row-forced">
            <Checkbox
                :trueValue="1" :falseValue="0"
                v-model="twoFactorAuthentication"
                :label="null"
                labelClasses="title-s text-rothcoCharcoal"
            />
            <p>
                {{ twoFactorAuthentication ? 'Enabled' : 'Disabled' }}
            </p>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, computed, watch, inject } from "vue";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, object } from 'yup';
    import { useFetch } from "@/helpers/composables/useFetch";
    import NProgress from "nprogress";
    import RothcoCta from "@/components/RothcoCta.vue";
    import PasswordInput from "@/components/PasswordInput.vue";
    import Checkbox from "@/components/Checkbox.vue";
    import { usePage } from "@inertiajs/vue3";
    
    let route = inject("route");
    const form = useForm({
        useInertia: true,
        // inertiaAlias: "ChangePassword",
        schema: object({
            new_password: string().required('The password field is required').min(8, 'Password must be at least 8 characters')
                .test('uppercase', 'Password must contain at least one uppercase letter', (value) => {
                    return /[A-Z]/.test(value);
                }).test('lowercase', 'Password must contain at least one lowercase letter', (value) => {
                    return /[a-z]/.test(value);
                }).test('number', 'Password must contain at least one number', (value) => {
                    return /[0-9]/.test(value);
                }).test('special', 'Password must contain at least one special character (# ? ! @ $  % ^ & * -)', (value) => {
                    return /[#?!@$%^&*-]/.test(value);
                }).test('spaces', 'Password must not contain spaces', (value) => {
                    return !/\s/.test(value);
                }).test('weak', 'Password is too weak', (value) => {
                    return !/password/i.test(value);
                }).default(""),
            confirm_new_password: string().required('Password confirmation is required').test({
                    name: "confirm_new_password",
                    message: "Password confirmation does not match",
                    test: (value) => {
                        return value == form.new_password;
                    }
            }).default(''),
        })
    });
    let show_success = ref(false);
    let showSuccess = () => {
        show_success.value = true
    };
    let canSubmitForm = computed(()=>{
        return !Object.keys(form.errors).length;
    });
    const resetPassword = (password, {afterFetch}) => {
        const { data } = useFetch(route('account.resetpassword'), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options.headers = {
                    ...ctx.options.headers,
                    Accept: 'application/json',
                }
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: async (ctx) => {
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
                if(ctx.data.error){
                    form.errors.new_password = ctx.data.error.password
                }
                return ctx;
            }
        }).post({
            ...password
        }).json();
        return data;
    };
    let submitForm = async () => {
        if(form.processing) return;
        
        await form.validate();
        if(canSubmitForm.value){
            resetPassword({password: form.new_password, password_confirmation: form.confirm_new_password}, {
                afterFetch: (ctx) => {
                    showSuccess();
                }
            });
        }
    };

    const twoFactorAuthentication = ref(usePage()?.props?.user?.fa_enabled);

    const userUpdatePayload = computed(()=>{
        return {
            user: {
                fa_enabled: twoFactorAuthentication.value
            }
        }
    })

    const { canAbort, abort, execute:updateUser } = useFetch(
        route("endpoint.user.update", usePage()?.props?.user?.user_id),
        {
            beforeFetch(ctx) {
                ctx.options.headers = {
                    ...ctx.options.headers,
                    Accept: 'application/json',
                }
                return ctx;
            },
        },
        {
            immediate: false,
            beforeFetch: (ctx) => {
                ctx.options.headers = {
                    ...ctx.options.headers,
                    Accept: 'application/json',
                }
            },
        }
    )
    .put(userUpdatePayload)
    .json();

    watch(()=>twoFactorAuthentication.value, (newVal, oldVal)=>{
        if(newVal != oldVal){
            if (canAbort.value) {
                abort();
            }
            updateUser();
        }
    })

</script>
<style lang="scss" scoped>
@import "../../assets/styles/variables.scss";
.password-change {
    @media (min-width: $breakpoint-desktop) {
        width: 50%;
    }
}
</style>